import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import CloseRoundedIcon
  from '@mui/icons-material/CloseRounded';
import OpenInNewRoundedIcon
  from '@mui/icons-material/OpenInNewRounded';

import {
  getMarginCenterCustomerKpiBreakdown,
} from '../services/marginCenterService';

import {
  formatMarginCenterCurrency,
  formatMarginCenterNumber,
} from '../utils/numberFormat';

import type {
  MarginCenterComparisonMode,
} from '../types/marginCenter';

import type {
  MarginCenterCustomerKpiBreakdownApiMetric,
  MarginCenterCustomerKpiBreakdownDirection,
  MarginCenterCustomerKpiBreakdownParams,
  MarginCenterCustomerKpiBreakdownRow,
} from '../types/customerKpiBreakdown';


export type CustomerKpiBreakdownMetric =
  | 'revenue'
  | 'margin_amount'
  | 'margin'
  | 'active_products'
  | 'units';


interface CustomerKpiBreakdownModalProps {
  open: boolean;
  metric: CustomerKpiBreakdownMetric;
  customerId: string;

  startDate?: string | null;
  endDate?: string | null;

  comparisonMode: MarginCenterComparisonMode;
  comparisonStartDate?: string | null;
  comparisonEndDate?: string | null;

  currencyCode: string | null;

  onClose: () => void;
  onOpenRelationship: (productId: string) => void;
}


function apiMetric(
  metric: CustomerKpiBreakdownMetric,
): MarginCenterCustomerKpiBreakdownApiMetric {
  return metric === 'margin'
    ? 'margin_percentage'
    : metric;
}


function formatPercentage(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${formatMarginCenterNumber(value, 2)} %`;
}


function formatSignedCurrency(
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  const formatted = formatMarginCenterCurrency(
    Math.abs(value),
    currencyCode,
  );

  if (value > 0) return `+${formatted}`;
  if (value < 0) return `−${formatted}`;

  return formatted;
}


function formatSignedNumber(
  value: number | null | undefined,
  maximumFractionDigits = 2,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  const formatted = formatMarginCenterNumber(
    Math.abs(value),
    maximumFractionDigits,
  );

  if (value > 0) return `+${formatted}`;
  if (value < 0) return `−${formatted}`;

  return formatted;
}


function formatSignedPoints(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${formatSignedNumber(value, 2)} pp`;
}


function movementColor(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
    || value === 0
  ) {
    return '#cbd5e1';
  }

  return value > 0
    ? '#34d399'
    : '#fb7185';
}


function modalTitle(
  metric: CustomerKpiBreakdownMetric,
): string {
  if (metric === 'revenue') {
    return 'Productos detrás del cambio de ingresos';
  }

  if (metric === 'margin_amount') {
    return 'Productos detrás del cambio de margen';
  }

  if (metric === 'margin') {
    return 'Productos detrás del cambio de margen %';
  }

  if (metric === 'active_products') {
    return 'Productos que cambian de actividad';
  }

  return 'Productos detrás del cambio de unidades';
}


function modalSubtitle(
  metric: CustomerKpiBreakdownMetric,
): string {
  if (metric === 'revenue') {
    return 'La suma de las aportaciones de los productos coincide con el cambio total de ingresos.';
  }

  if (metric === 'margin_amount') {
    return 'Margen calculado como ingresos menos coste conocido. La suma por producto coincide con el cambio total.';
  }

  if (metric === 'margin') {
    return 'Cada fila muestra cuánto aporta ese producto al cambio del margen % total del cliente.';
  }

  if (metric === 'active_products') {
    return 'Los productos solo actuales menos los solo comparados explican el cambio neto de productos activos.';
  }

  return 'La suma de los cambios de unidades por producto coincide con el cambio total del cliente.';
}


function formatSummaryValue(
  metric: CustomerKpiBreakdownMetric,
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (
    metric === 'revenue'
    || metric === 'margin_amount'
  ) {
    return formatMarginCenterCurrency(
      value,
      currencyCode,
    );
  }

  if (metric === 'margin') {
    return formatPercentage(value);
  }

  if (metric === 'active_products') {
    return formatMarginCenterNumber(
      value,
      0,
    );
  }

  return formatMarginCenterNumber(
    value,
    2,
  );
}


function formatSummaryDelta(
  metric: CustomerKpiBreakdownMetric,
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (
    metric === 'revenue'
    || metric === 'margin_amount'
  ) {
    return formatSignedCurrency(
      value,
      currencyCode,
    );
  }

  if (metric === 'margin') {
    return formatSignedPoints(value);
  }

  if (metric === 'active_products') {
    return formatSignedNumber(
      value,
      0,
    );
  }

  return formatSignedNumber(
    value,
    2,
  );
}


function formatContribution(
  metric: CustomerKpiBreakdownMetric,
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  return formatSummaryDelta(
    metric,
    value,
    currencyCode,
  );
}


function rowCurrent(
  row: MarginCenterCustomerKpiBreakdownRow,
  metric: CustomerKpiBreakdownMetric,
  currencyCode: string | null,
): string {
  if (
    metric === 'revenue'
    || metric === 'margin_amount'
  ) {
    return formatMarginCenterCurrency(
      row.metric_current_value,
      currencyCode,
    );
  }

  if (metric === 'margin') {
    return formatPercentage(
      row.current_margin_percentage,
    );
  }

  if (metric === 'active_products') {
    return formatMarginCenterCurrency(
      row.current_revenue,
      currencyCode,
    );
  }

  return formatMarginCenterNumber(
    row.metric_current_value,
    2,
  );
}


function rowComparison(
  row: MarginCenterCustomerKpiBreakdownRow,
  metric: CustomerKpiBreakdownMetric,
  currencyCode: string | null,
): string {
  if (
    metric === 'revenue'
    || metric === 'margin_amount'
  ) {
    return formatMarginCenterCurrency(
      row.metric_comparison_value,
      currencyCode,
    );
  }

  if (metric === 'margin') {
    return formatPercentage(
      row.comparison_margin_percentage,
    );
  }

  if (metric === 'active_products') {
    return formatMarginCenterCurrency(
      row.comparison_revenue,
      currencyCode,
    );
  }

  return formatMarginCenterNumber(
    row.metric_comparison_value,
    2,
  );
}


function rowChange(
  row: MarginCenterCustomerKpiBreakdownRow,
  metric: CustomerKpiBreakdownMetric,
  direction: MarginCenterCustomerKpiBreakdownDirection,
  currencyCode: string | null,
): string {
  if (metric === 'active_products') {
    return direction === 'up'
      ? 'Solo actual'
      : 'Solo comparado';
  }

  return formatContribution(
    metric,
    row.contribution_value,
    currencyCode,
  );
}


function currentHeader(
  metric: CustomerKpiBreakdownMetric,
): string {
  if (metric === 'margin_amount') {
    return 'Margen actual';
  }

  if (metric === 'margin') {
    return 'Margen actual';
  }

  if (metric === 'active_products') {
    return 'Ingresos actuales';
  }

  if (metric === 'units') {
    return 'Unidades actuales';
  }

  return 'Ingresos actuales';
}


function comparisonHeader(
  metric: CustomerKpiBreakdownMetric,
): string {
  if (metric === 'margin_amount') {
    return 'Margen comparación';
  }

  if (metric === 'margin') {
    return 'Margen comparación';
  }

  if (metric === 'active_products') {
    return 'Ingresos comparación';
  }

  if (metric === 'units') {
    return 'Unidades comparación';
  }

  return 'Ingresos comparación';
}


function changeHeader(
  metric: CustomerKpiBreakdownMetric,
): string {
  if (metric === 'margin_amount') {
    return 'Δ margen';
  }

  if (metric === 'margin') {
    return 'Aporta al cambio';
  }

  if (metric === 'active_products') {
    return 'Situación';
  }

  if (metric === 'units') {
    return 'Δ unidades';
  }

  return 'Δ ingresos';
}


export default function CustomerKpiBreakdownModal({
  open,
  metric,
  customerId,
  startDate,
  endDate,
  comparisonMode,
  comparisonStartDate,
  comparisonEndDate,
  currencyCode,
  onClose,
  onOpenRelationship,
}: CustomerKpiBreakdownModalProps) {
  const [direction, setDirection] =
    useState<MarginCenterCustomerKpiBreakdownDirection>(
      'up',
    );

  useEffect(() => {
    if (open) {
      setDirection('up');
    }
  }, [metric, open]);

  const params =
    useMemo<MarginCenterCustomerKpiBreakdownParams>(
      () => ({
        metric: apiMetric(metric),
        direction,
        start_date:
          startDate ?? undefined,
        end_date:
          endDate ?? undefined,
        comparison_mode:
          comparisonMode,
        comparison_start_date:
          comparisonMode === 'custom'
            ? comparisonStartDate ?? undefined
            : undefined,
        comparison_end_date:
          comparisonMode === 'custom'
            ? comparisonEndDate ?? undefined
            : undefined,
        limit: 25,
      }),
      [
        comparisonEndDate,
        comparisonMode,
        comparisonStartDate,
        direction,
        endDate,
        metric,
        startDate,
      ],
    );

  const breakdownQuery = useQuery({
    queryKey: [
      'margin-center',
      'customer-kpi-breakdown',
      customerId,
      params,
    ],
    queryFn: () =>
      getMarginCenterCustomerKpiBreakdown(
        customerId,
        params,
      ),
    enabled:
      open
      && Boolean(customerId)
      && comparisonMode !== 'none',
    retry: false,
  });

  const data = breakdownQuery.data;
  const summary = data?.summary ?? null;
  const rows = data?.rows ?? [];

  const upCount =
    summary?.up_count ?? 0;

  const downCount =
    summary?.down_count ?? 0;

  const directionLabels =
    metric === 'active_products'
      ? {
          up: `Solo actual (${upCount})`,
          down: `Solo comparado (${downCount})`,
        }
      : {
          up: `Suben (${upCount})`,
          down: `Bajan (${downCount})`,
        };

  const currentLabel =
    metric === 'active_products'
      ? 'Productos actuales'
      : 'Actual';

  const comparisonLabel =
    metric === 'active_products'
      ? 'Productos comparación'
      : 'Comparación';

  const netLabel =
    metric === 'active_products'
      ? 'Cambio neto'
      : 'Cambio';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: {
            xs: '94vw',
            sm: 920,
          },
          maxWidth: '94vw',
          height: {
            xs: '82vh',
            sm: 720,
          },
          maxHeight: '82vh',
          bgcolor: '#0b1220',
          border:
            '1px solid rgba(148,163,184,0.18)',
          borderRadius: 3,
          boxShadow:
            '0 24px 80px rgba(2,6,23,0.56)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          height: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          p: {
            xs: 2,
            md: 2.5,
          },
        }}
      >
        <Stack
          spacing={1.5}
          sx={{
            height: '100%',
            minHeight: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                {modalTitle(metric)}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.35,
                  color: '#94a3b8',
                  lineHeight: 1.5,
                }}
              >
                {modalSubtitle(metric)}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Cerrar"
              sx={{
                color: '#94a3b8',
                flexShrink: 0,
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          {comparisonMode === 'none' ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'grid',
                placeItems: 'center',
                border:
                  '1px dashed rgba(148,163,184,0.18)',
                borderRadius: 2.5,
                bgcolor:
                  'rgba(15,23,42,0.24)',
              }}
            >
              <Stack
                spacing={0.5}
                sx={{
                  px: 3,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#e2e8f0',
                    fontWeight: 850,
                  }}
                >
                  No hay comparación activa
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: '#94a3b8' }}
                >
                  Activa una comparación temporal para estudiar qué productos están detrás del cambio.
                </Typography>
              </Stack>
            </Box>
          ) : (
            <>
              {summary && (
                <Stack spacing={0.85}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(3, minmax(0, 1fr))',
                      },
                      gap: 0.75,
                    }}
                  >
                    {[
                      {
                        label: currentLabel,
                        value: formatSummaryValue(
                          metric,
                          summary.current_value,
                          currencyCode,
                        ),
                        color: '#f8fafc',
                      },
                      {
                        label: comparisonLabel,
                        value: formatSummaryValue(
                          metric,
                          summary.comparison_value,
                          currencyCode,
                        ),
                        color: '#94a3b8',
                      },
                      {
                        label: netLabel,
                        value: formatSummaryDelta(
                          metric,
                          summary.delta_value,
                          currencyCode,
                        ),
                        color: movementColor(
                          summary.delta_value,
                        ),
                      },
                    ].map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          px: 1.25,
                          py: 0.85,
                          borderRadius: 2,
                          border:
                            '1px solid rgba(148,163,184,0.12)',
                          bgcolor:
                            'rgba(15,23,42,0.26)',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                            fontWeight: 750,
                          }}
                        >
                          {item.label}
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            mt: 0.15,
                            color: item.color,
                            fontWeight: 900,
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#94a3b8',
                    }}
                  >
                    {metric === 'active_products'
                      ? `${upCount} solo actual − ${downCount} solo comparado = ${formatSummaryDelta(
                          metric,
                          summary.delta_value,
                          currencyCode,
                        )} netos`
                      : `${formatContribution(
                          metric,
                          summary.up_contribution_value,
                          currencyCode,
                        )} de subidas ${formatContribution(
                          metric,
                          summary.down_contribution_value,
                          currencyCode,
                        )} de bajadas = ${formatSummaryDelta(
                          metric,
                          summary.delta_value,
                          currencyCode,
                        )} neto`}
                    {summary.reconciled === true
                      ? ' · cálculo reconciliado'
                      : ''}
                  </Typography>

                  {(metric === 'margin'
                    || metric === 'margin_amount')
                    && summary.cost_coverage
                    && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#64748b',
                        }}
                      >
                        Margen calculado con ingresos cuyo coste está resuelto: {formatPercentage(
                          summary.cost_coverage.current_revenue_pct,
                        )} actual · {formatPercentage(
                          summary.cost_coverage.comparison_revenue_pct,
                        )} comparación.
                      </Typography>
                    )}
                </Stack>
              )}

              <ToggleButtonGroup
                exclusive
                size="small"
                value={direction}
                onChange={(
                  _,
                  nextDirection:
                    | MarginCenterCustomerKpiBreakdownDirection
                    | null,
                ) => {
                  if (nextDirection) {
                    setDirection(nextDirection);
                  }
                }}
                sx={{
                  alignSelf: 'flex-start',

                  '& .MuiToggleButton-root': {
                    px: 1.5,
                    py: 0.55,
                    color: '#94a3b8',
                    borderColor:
                      'rgba(148,163,184,0.18)',
                    textTransform: 'none',
                  },

                  '& .Mui-selected': {
                    color: '#f8fafc !important',
                    bgcolor:
                      'rgba(56,189,248,0.12) !important',
                  },
                }}
              >
                <ToggleButton value="up">
                  {directionLabels.up}
                </ToggleButton>

                <ToggleButton value="down">
                  {directionLabels.down}
                </ToggleButton>
              </ToggleButtonGroup>

              {breakdownQuery.isFetching && (
                <LinearProgress
                  sx={{ flexShrink: 0 }}
                />
              )}

              {breakdownQuery.isError && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Alert
                    severity="error"
                    sx={{
                      width: '100%',
                      maxWidth: 560,
                    }}
                  >
                    No se ha podido cargar el detalle de este indicador.
                  </Alert>
                </Box>
              )}

              {!breakdownQuery.isError
                && !breakdownQuery.isFetching
                && data?.status ===
                  'insufficient_evidence'
                && (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <Alert
                      severity="info"
                      sx={{
                        width: '100%',
                        maxWidth: 620,
                      }}
                    >
                      No existe evidencia suficiente para reconciliar este indicador en la comparación seleccionada.
                    </Alert>
                  </Box>
                )}

              {!breakdownQuery.isError
                && !breakdownQuery.isFetching
                && data
                && data.status !==
                  'insufficient_evidence'
                && rows.length === 0 && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'grid',
                    placeItems: 'center',
                    border:
                      '1px dashed rgba(148,163,184,0.18)',
                    borderRadius: 2.5,
                    bgcolor:
                      'rgba(15,23,42,0.24)',
                  }}
                >
                  <Stack
                    spacing={0.5}
                    sx={{
                      px: 3,
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: '#e2e8f0',
                        fontWeight: 850,
                      }}
                    >
                      No hay productos en esta dirección
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: '#94a3b8',
                      }}
                    >
                      {metric === 'active_products'
                        ? direction === 'up'
                          ? 'No hay productos con actividad únicamente en el periodo actual.'
                          : 'No hay productos con actividad únicamente en el periodo comparado.'
                        : direction === 'up'
                          ? 'No hay productos con aportación positiva a este cambio.'
                          : 'No hay productos con aportación negativa a este cambio.'}
                    </Typography>
                  </Stack>
                </Box>
              )}

              {rows.length > 0 && data && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      flexShrink: 0,
                    }}
                  >
                    Mostrando {data.page.returned_count} de {data.page.total_count} productos de esta dirección.
                  </Typography>

                  <TableContainer
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      height: '100%',
                      border:
                        '1px solid rgba(148,163,184,0.14)',
                      borderRadius: 2.5,
                      overflowX: 'auto',
                      overflowY: 'auto',
                    }}
                  >
                    <Table
                      stickyHeader
                      size="small"
                      sx={{
                        minWidth: 680,

                        '& .MuiTableCell-head': {
                          bgcolor: '#111827',
                          color: '#e2e8f0',
                          fontWeight: 850,
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            Producto
                          </TableCell>

                          <TableCell align="right">
                            {currentHeader(metric)}
                          </TableCell>

                          <TableCell align="right">
                            {comparisonHeader(metric)}
                          </TableCell>

                          <TableCell align="right">
                            {changeHeader(metric)}
                          </TableCell>

                          <TableCell align="right">
                            Acción
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {rows.map((row, index) => (
                          <TableRow
                            key={
                              row.product_id
                              ?? `${row.label}-${index}`
                            }
                            hover
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#f8fafc',
                                  fontWeight: 750,
                                }}
                              >
                                {row.label}
                              </Typography>
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                color: '#cbd5e1',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {rowCurrent(
                                row,
                                metric,
                                currencyCode,
                              )}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                color: '#94a3b8',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {rowComparison(
                                row,
                                metric,
                                currencyCode,
                              )}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    movementColor(
                                      row.contribution_value,
                                    ),
                                  fontWeight: 850,
                                }}
                              >
                                {rowChange(
                                  row,
                                  metric,
                                  direction,
                                  currencyCode,
                                )}
                              </Typography>

                              {metric !==
                                'active_products'
                                && row
                                  .contribution_share_absolute_pct
                                  !== null && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: '#64748b',
                                  }}
                                >
                                  {formatPercentage(
                                    row
                                      .contribution_share_absolute_pct,
                                  )} del movimiento
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell align="right">
                              {row.can_open_relationship
                                && row.product_id
                                ? (
                                  <Button
                                    size="small"
                                    variant="text"
                                    endIcon={
                                      <OpenInNewRoundedIcon />
                                    }
                                    onClick={() => {
                                      onOpenRelationship(
                                        row.product_id!,
                                      );
                                      onClose();
                                    }}
                                  >
                                    Examinar
                                  </Button>
                                )
                                : (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#64748b',
                                    }}
                                  >
                                    —
                                  </Typography>
                                )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
